import React, { useEffect, useState } from "react";
import { Badge, Card, List, Select, Space, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PaginationTable, ReferralInvitation } from "js/components/types";
import "styles/components/Dashboard/TableDashboard.scss";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import Title from "antd/es/typography/Title";
import type { SelectProps } from "antd";

interface Props {
  data: ReferralInvitation[];
  setData: (data: ReferralInvitation[]) => void;
  setPagination: (pagination: PaginationTable) => void;
  pagination: PaginationTable;
  loading: boolean;
}

const ReferralInvitationsTable: React.FC<Props> = ({
  data,
  setData,
  pagination,
  setPagination,
  loading,
}) => {
  const screen = useBreakpoint();
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<ReferralInvitation[]>([]);

  const columns: ColumnsType<ReferralInvitation> = [
    {
      title: "Fecha de Creación",
      dataIndex: "created_at",
      render: (date) =>
        new Date(date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      align: "center",
      width: "4%",
    },
    {
      title: "Fecha ult. actualización",
      dataIndex: "updated_at",
      render: (date) =>
        new Date(date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      align: "center",
      width: "4%",
    },
    {
      title: "Correo",
      dataIndex: "receiver_email",
      width: "5%",
    },
    {
      title: "Estatus",
      dataIndex: "status",
      filters: [
        {
          text: "Pendiente",
          value: "pending",
        },
        {
          text: "Cuenta Creada",
          value: "registered",
        },
        {
          text: "Cuenta Verificada",
          value: "verified",
        },
      ],
      onFilter: (value: string, record) => record.status.indexOf(value) === 0,
      align: "left",
      render: (value) => {
        if (value == "pending") {
          return <Badge status="processing" text="Invitación enviada" />;
        } else {
          if (value == "registered") {
            return <Badge status="warning" text="Cuenta Creada" />;
          } else {
            return <Badge status="success" text="Cuenta Verificada" />;
          }
        }
      },
      width: "5%",
    },
  ];

  const filterOptions: SelectProps["options"] = [
    {
      label: "Invitación enviada",
      value: "pending",
    },
    {
      label: "Cuenta Creada",
      value: "registered",
    },
    {
      label: "Cuenta Verificada",
      value: "verified",
    },
  ];

  const renderStatusBadge = (status) => {
    if (status === "pending") {
      return <Badge status="processing" text="Invitación enviada" />;
    } else if (status === "registered") {
      return <Badge status="warning" text="Cuenta Creada" />;
    } else if (status === "verified") {
      return <Badge status="success" text="Cuenta Verificada" />;
    }
    // Add more conditions if needed
    return null;
  };

  const handleTableChange = (pagination: PaginationTable) => {
    setPagination(pagination);
  };

  const handleSelectChange = (value: string[]) => {
    setSelectedStatus(value);

    // If no statuses are selected, show all data
    if (value.length === 0) {
      setFilteredData(data);
    } else {
      // Filter the data based on selected statuses
      const newData = data.filter((item) => value.includes(item.status));
      setFilteredData(newData);
    }
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  return (
    <>
      {!screen.md ? (
        <div>
          <Select
            placeholder="Filtrar por Estatus"
            mode={"multiple"}
            allowClear
            onChange={handleSelectChange}
            style={{ marginBottom: 16, width: "40%" }}
            options={filterOptions}
          ></Select>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 4,
              xxl: 3,
            }}
            dataSource={filteredData}
            renderItem={(item) => (
              <List.Item>
                <Card title={<>Estatus: {renderStatusBadge(item.status)}</>}>
                  <Space direction={"vertical"}>
                    <div>
                      <strong>Correo del invitado:</strong>{" "}
                      {item.receiver_email}
                    </div>
                    <div>
                      <strong>Creado:</strong>{" "}
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString(
                            "es-ES",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : ""}
                    </div>
                    <div>
                      <strong>Actualizado:</strong>{" "}
                      {item.updated_at
                        ? new Date(item.created_at).toLocaleDateString(
                            "es-ES",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : ""}
                    </div>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        </div>
      ) : (
        <>
          <Table
            size={"small"}
            columns={columns}
            rowKey={(record) => record.id ?? ""}
            dataSource={data}
            pagination={pagination}
            onChange={handleTableChange}
            loading={loading}
            scroll={{ y: 750, x: "max-content" }}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      textAlign:
                        props.children.includes("Acciones") ||
                        props.children.includes("Tipo") ||
                        props.children.includes("Fecha de Creación")
                          ? "center"
                          : undefined,
                    }}
                  />
                ),
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default ReferralInvitationsTable;
